/**
 * Camera storage keys – per-device history of the ENCRYPTED `master_key` secret
 * used to decrypt cloud recordings and thumbnails.
 *
 * @remarks
 * This store NEVER holds the plaintext `master_key`. It persists only the
 * password-encrypted backups (`{ blob, hmac }`); the decrypted `master_key` lives
 * in the in-memory {@link camera_storage_resolved_keys} store. The PBKDF2 salt
 * rides inside each `blob` (`salt || iv || ciphertext+tag`), so no separate salt /
 * iterations field is stored.
 *
 * Keys rotate, so each device stores the API's backup history (`EncryptedMasterKey[]`,
 * newest first) as-is; {@link pickBackupForDate} selects the backup active at a
 * recording's date.
 */

import { get, writable } from 'svelte/store';

/** localStorage key under which per-device encrypted `master_key` backups are stored. */
const STORAGE_KEY = 'camera_storage_keys';

/** The password-encrypted `master_key` backup, exactly as the API returns it. */
export interface EncryptedMasterKey {
	/** base64(`salt || iv || ciphertext || gcmTag`) of the AES-256-GCM encrypted `master_key`. */
	blob: string;
	/** base64 HMAC-SHA256 of the plaintext `master_key`, keyed by the PBKDF2-derived key. */
	hmac: string;
	/** ISO timestamp the backup was created – the era used for date-based selection. */
	createdAt: string;
}

/** Map of `deviceId` -> encrypted `master_key` backup history (newest first, as the API returns). */
type CameraStorageKeyMap = Record<string, EncryptedMasterKey[]>;

/** Parses an ISO timestamp to epoch ms, or `NaN` when missing/unparseable. */
const toEpoch = (value?: string): number => (value ? new Date(value).getTime() : NaN);

/**
 * Selects the backup active at a recording's `date` — the newest entry whose
 * `createdAt` is at or before `date`, compared by parsed epoch (so differing but
 * equivalent ISO/UTC formats still order correctly). Assumes `backups` is
 * newest-first (the API order); falls back to the oldest backup when `date`
 * precedes every entry, and to the newest when `date` is omitted or either side
 * is an unparseable date.
 *
 * @param backups - The device's backup history (newest first).
 * @param date - The recording's ISO `date`, or empty/undefined for device-level.
 * @returns The chosen backup, or `null` when there are no backups.
 */
export const pickBackupForDate = (
	backups: EncryptedMasterKey[],
	date?: string,
): EncryptedMasterKey | null => {
	if (!backups.length) return null;
	const dateEpoch = toEpoch(date);
	// No/invalid date → newest era (device-level and safe default).
	if (Number.isNaN(dateEpoch)) return backups[0];
	const active = backups.find((backup) => {
		const createdEpoch = toEpoch(backup.createdAt);
		// An unparseable `createdAt` can't be proven to precede the date; skip it
		// rather than mis-select it as active.
		return !Number.isNaN(createdEpoch) && createdEpoch <= dateEpoch;
	});
	return active ?? backups[backups.length - 1];
};

const readInitial = (): CameraStorageKeyMap => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? (parsed as CameraStorageKeyMap) : {};
	} catch {
		return {};
	}
};

function create_camera_storage_keys() {
	const { subscribe, set, update } = writable<CameraStorageKeyMap>(readInitial());

	return {
		subscribe,
		set,
		/** Prepends a freshly provisioned/rotated backup as the newest entry. */
		setEncrypted: (deviceId: string, key: EncryptedMasterKey) => {
			if (!deviceId || !key?.blob) return;
			update((state) => ({ ...state, [deviceId]: [key, ...(state[deviceId] ?? [])] }));
		},
		/** Replaces a device's entire backup history with the API array (newest first). */
		setAll: (deviceId: string, keys: EncryptedMasterKey[]) => {
			if (!deviceId) return;
			update((state) => ({ ...state, [deviceId]: Array.isArray(keys) ? keys : [] }));
		},
		/** Returns the device's full backup history (newest first), or an empty array. */
		getAll: (deviceId: string): EncryptedMasterKey[] => {
			if (!deviceId) return [];
			return get(camera_storage_keys)[deviceId] ?? [];
		},
		/** Returns the device's newest backup, or `null` when none. */
		getEncrypted: (deviceId: string): EncryptedMasterKey | null => {
			if (!deviceId) return null;
			return get(camera_storage_keys)[deviceId]?.[0] ?? null;
		},
		/**
		 * Returns the backup for a recording's `date` (era active at that date), or
		 * `null` when the device has no backups. See {@link pickBackupForDate}.
		 */
		getForDate: (deviceId: string, date?: string): EncryptedMasterKey | null => {
			if (!deviceId) return null;
			return pickBackupForDate(get(camera_storage_keys)[deviceId] ?? [], date);
		},
		remove: (deviceId: string) =>
			update((state) => {
				delete state[deviceId];
				return state;
			}),
		clear: () => set({}),
	};
}

export const camera_storage_keys = create_camera_storage_keys();
camera_storage_keys.subscribe((val) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(val));
	} catch {
		/** storage may be unavailable (private mode / quota) */
	}
});
